/**
 * Error taxonomy — typed classification system for the whole frontend.
 * Drives both UI display and Sentry severity levels.
 */
import { isAxiosError } from 'axios'
import { isRecord, isString } from '@/utils/typeGuards'

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/** Canonical error kinds — maps directly to Sentry severity and UI presentation. */
export type ErrorKind =
  | 'network'     // No connection / timeout / DNS failure
  | 'auth'        // 401 / 403 — session expired, forbidden
  | 'validation'  // 400 / 422 — bad user input
  | 'business'    // 409 / domain-specific logic errors
  | 'server'      // 500+ — backend crash
  | 'unknown'     // Catch-all

export interface ClassifiedError {
  kind: ErrorKind
  /** User-friendly message (already i18n-translated or enriched). */
  message: string
  /** Raw technical message for Sentry / console. */
  technicalMessage: string
  statusCode?: number
  originalError: unknown
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function kindFromStatus(status: number): ErrorKind {
  if (status === 0) return 'network'
  if (status === 401 || status === 403) return 'auth'
  if (status === 400 || status === 422) return 'validation'
  if (status === 409) return 'business'
  if (status >= 500) return 'server'
  return 'unknown'
}

/**
 * Resolve a user-friendly message from the enriched error chain produced by
 * `api.service.ts`.  The interceptor stores the translated message at
 * `error.userFriendly.message`.
 */
function extractUserFriendlyMessage(error: unknown): string | null {
  if (!isRecord(error)) return null

  // Direct userFriendly on the error (set by api.service interceptor)
  const userFriendly = error['userFriendly']
  if (isRecord(userFriendly) && isString(userFriendly['message'])) {
    return userFriendly['message']
  }

  // Nested in cause
  const cause = error['cause']
  if (isRecord(cause)) {
    const uf = cause['userFriendly']
    if (isRecord(uf) && isString(uf['message'])) {
      return uf['message']
    }
  }

  // Raw FastAPI detail (public API instance has no enriching interceptor):
  // the backend already writes actionable French messages there (quota
  // atteint, paiement non configuré, place indisponible...).
  return extractResponseDetail(error['response'])
}

/**
 * Message porté par le `detail` d'une réponse FastAPI.
 *
 * Le `detail` prend deux formes : une chaîne pour la plupart des erreurs, et un
 * **objet** pour les conflits du tunnel de réservation (409) —
 * `{message, unavailable_spot_ids}` pour les postes,
 * `{message, unavailable_option_ids, options}` pour les options.
 *
 * Seule la forme chaîne était lue : les messages de conflit, pourtant précis,
 * n'atteignaient jamais le client, qui voyait « Erreur lors de la réservation.
 * Veuillez réessayer. » sans savoir quoi corriger.
 */
function extractResponseDetail(response: unknown): string | null {
  if (!isRecord(response)) return null

  const data = response['data']
  if (!isRecord(data)) return null

  const detail = data['detail']
  if (isString(detail)) return detail
  if (isRecord(detail) && isString(detail['message'])) return detail['message']

  return null
}

function extractTechnicalMessage(error: unknown): string {
  if (isRecord(error) && isString(error['message'])) {
    return error['message']
  }
  if (error instanceof Error) {
    return error.message
  }
  return String(error)
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Classify an unknown error.  Never throws — always returns a ClassifiedError.
 * Works with Axios errors, plain Error objects, and arbitrary values.
 */
export function classifyError(error: unknown, fallbackMessage: string): ClassifiedError {
  try {
    // -- Axios error path ---------------------------------------------------
    if (isAxiosError(error)) {
      const status = error.response?.status ?? 0
      const kind: ErrorKind = error.response ? kindFromStatus(status) : 'network'
      const userFriendlyMsg = extractUserFriendlyMessage(error) ?? fallbackMessage
      const technicalMessage = error.message

      return {
        kind,
        message: userFriendlyMsg,
        technicalMessage,
        statusCode: error.response?.status,
        originalError: error,
      }
    }

    // -- Plain Error path ---------------------------------------------------
    const userFriendlyMsg = extractUserFriendlyMessage(error)
    const technicalMessage = extractTechnicalMessage(error)

    return {
      kind: 'unknown',
      message: userFriendlyMsg ?? (isString(technicalMessage) && technicalMessage.trim().length > 0
        ? technicalMessage
        : fallbackMessage),
      technicalMessage,
      originalError: error,
    }
  } catch {
    // Safety net — classifyError must NEVER throw
    return {
      kind: 'unknown',
      message: fallbackMessage,
      technicalMessage: 'classifyError internal failure',
      originalError: error,
    }
  }
}
